import i18n from "i18next";
import * as Sentry from "@sentry/react";
import {NotificationManager} from "react-notifications";

/**
 * @typedef {object} MonthPaymentTotals
 * @property {number} month
 * @property {number} year
 * @property {number} Total
 * @property {number} TotalInCreditCard
 * @property {number} TotalInCheque
 * @property {number} TotalFuture
 * */

// A cold dashboard key is rebuilt by a background worker; the API answers 202 {calculating: true}
// until the value is ready. For the largest partners the rebuild takes about 2.5 minutes.
const DEFAULT_RETRY_AFTER_SECONDS = 10;
const MAX_WAIT_FOR_REBUILD_MS = 5 * 60 * 1000;
/** greppable marker */
const LOG = 'DASHBOARD_REBUILD_WAIT';

export class PartnerDashboardApi {
    #client;

    constructor(client) {
        this.#client = client;
    }

    /**
     * GET that waits while the server reports the value is still being calculated.
     * `asyncRebuild` tells the API this client understands the "calculating" answer.
     * @param {string} uri
     * */
    async #getWaitingForRebuild(uri) {
        const startedAt = Date.now();
        let notified = false;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const data = await this.#client.get(uri, { asyncRebuild: true }, { throwError: true });
            if (!data?.calculating) {
                if (notified) console.log(`${LOG} got the value for ${uri} after ${Date.now() - startedAt}ms`);
                return data;
            }
            if (Date.now() - startedAt >= MAX_WAIT_FOR_REBUILD_MS) {
                console.log(`${LOG} gave up for ${uri} after ${Date.now() - startedAt}ms`);
                Sentry.captureMessage(`${LOG} gave up waiting for ${uri}`);
                throw new Error(i18n.t('buildings.dashboard_calculating_timeout'));
            }
            if (!notified) {
                notified = true;
                console.log(`${LOG} server is calculating ${uri}. will retry`);
                NotificationManager.info(i18n.t('buildings.dashboard_calculating'), '', 10000);
            }
            const retryAfterSeconds = Number(data.retryAfterSeconds) > 0 ? Number(data.retryAfterSeconds) : DEFAULT_RETRY_AFTER_SECONDS;
            await new Promise(resolve => window.setTimeout(resolve, retryAfterSeconds * 1000));
        }
    }

    /**
     * @param {string | number} partnerId
     * */
    getStats(partnerId) {
        return this.#getWaitingForRebuild(`/partners/${partnerId}/dashboard/stats`);
    }

    /**
     * @param {string | number} partnerId
     * @return {Promise<{ monthTotals: MonthPaymentTotals[] }>}
     * */
    getMonthAmounts(partnerId) {
        return this.#getWaitingForRebuild(`/partners/${partnerId}/dashboard/month_amounts`);
    }
}
